import { createClient } from '@supabase/supabase-js';
import { INTRODUCTION_HTML } from './introductionContent';

const SUPABASE_URL = 'https://lpimrjarhgikirqedofj.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxwaW1yamFyaGdpa2lycWVkb2ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI5MDM4OTUsImV4cCI6MjA4ODQ3OTg5NX0.1AvhdRfm5AdDcknqAPlrw5r-8ByK-mhpNNFYAzTnhrM';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ==================== BIBLE DATA ====================

export const getBooks = async () => {
  const { data, error } = await supabase
    .from('bible_books')
    .select('*')
    .order('order');
  
  if (error) throw error;
  
  // Organize by testament
  const ancien_testament = data.filter(b => b.testament === 'ancien').map(b => ({
    id: b.id,
    name: b.name,
    abbr: b.abbr,
    chapters: b.chapters_count
  }));
  
  const nouveau_testament = data.filter(b => b.testament === 'nouveau').map(b => ({
    id: b.id,
    name: b.name,
    abbr: b.abbr,
    chapters: b.chapters_count
  }));
  
  return { ancien_testament, nouveau_testament };
};

export const getBook = async (bookId) => {
  const { data, error } = await supabase
    .from('bible_books')
    .select('*')
    .eq('id', bookId)
    .single();
  
  if (error) throw error;
  return {
    id: data.id,
    name: data.name,
    abbr: data.abbr,
    chapters: data.chapters_count,
    introduction: data.introduction
  };
};

export const getChapter = async (bookId, chapterNumber) => {
  const { data, error } = await supabase
    .from('bible_verses')
    .select('*')
    .eq('book_id', bookId)
    .eq('chapter_number', chapterNumber)
    .order('verse_number');
  
  if (error) throw error;
  
  return {
    book_id: bookId,
    chapter: chapterNumber,
    verses: data.map(v => ({
      verse: v.verse_number,
      text: v.french,
      latin: v.latin,
      has_comment: v.has_comment,
      comment: v.comment
    }))
  };
};

export const searchBible = async (query, limit = 50) => {
  const { data, error } = await supabase
    .from('bible_verses')
    .select(`
      book_id,
      chapter_number,
      verse_number,
      french,
      bible_books!inner(name)
    `)
    .ilike('french', `%${query}%`)
    .limit(limit);
  
  if (error) throw error;
  
  return {
    query,
    count: data.length,
    results: data.map(v => ({
      book_id: v.book_id,
      book_name: v.bible_books.name,
      chapter: v.chapter_number,
      verse: v.verse_number,
      text: v.french,
      highlight_start: v.french.toLowerCase().indexOf(query.toLowerCase()),
      highlight_end: v.french.toLowerCase().indexOf(query.toLowerCase()) + query.length
    }))
  };
};

// ==================== GARDE-PAGES ====================

export const getGardePages = async (userId) => {
  const { data, error } = await supabase
    .from('garde_pages')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const createGardePage = async (gardePage) => {
  // The DB has a DEFAULT on the id column. Generate one client-side as a
  // safety net for older browsers / contexts where crypto.randomUUID exists,
  // otherwise let the server pick one.
  const payload = { ...gardePage };
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    payload.id = crypto.randomUUID();
  }
  const { data, error } = await supabase
    .from('garde_pages')
    .insert(payload)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteGardePage = async (id) => {
  const { error } = await supabase
    .from('garde_pages')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
  return { success: true };
};

// ==================== SURLIGNAGES ====================

export const getSurlignages = async (userId, bookId = null) => {
  let query = supabase
    .from('surlignages')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  if (bookId) {
    query = query.eq('book_id', bookId);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const createSurlignage = async (surlignage) => {
  const id = crypto.randomUUID();
  const { data, error } = await supabase
    .from('surlignages')
    .insert({ ...surlignage, id })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteSurlignage = async (id) => {
  console.log('deleteSurlignage called with id:', id);
  const { data, error } = await supabase
    .from('surlignages')
    .delete()
    .eq('id', id)
    .select();
  
  console.log('Delete response - data:', data, 'error:', error);
  
  if (error) {
    console.error('Supabase delete error:', error);
    throw error;
  }
  
  return { success: true, deleted: data };
};

// ==================== NOTES ====================

export const getNotes = async (userId) => {
  const { data, error } = await supabase
    .from('notes')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const createNote = async (note) => {
  const { data, error } = await supabase
    .from('notes')
    .insert(note)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteNote = async (id) => {
  const { error } = await supabase
    .from('notes')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
  return { success: true };
};

// ==================== PREMIUM ====================

export const checkPremium = async (userId) => {
  const { data, error } = await supabase
    .from('premium_users')
    .select('*')
    .eq('user_id', userId)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error;
  return { is_premium: !!data };
};

export const activatePremium = async (userId, code) => {
  if (code !== 'FILLION1920') {
    throw new Error('Code invalide');
  }
  
  const { data, error } = await supabase
    .from('premium_users')
    .insert({ user_id: userId, method: 'code' })
    .select()
    .single();
  
  if (error) throw error;
  return { success: true };
};

// ==================== READINGS CACHE ====================

export const getReadingsCache = async (date) => {
  const { data, error } = await supabase
    .from('readings_cache')
    .select('*')
    .eq('date', date)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const saveReadingsCache = async (date, liturgy, readings) => {
  const { data, error } = await supabase
    .from('readings_cache')
    .upsert({ 
      date, 
      liturgy, 
      readings_json: readings,
      fetched_at: new Date().toISOString()
    })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// ==================== PREFACE CONTENT ====================

export const PREFACE_CONTENT = {
  dedicace: {
    title: "Dédicace",
    subtitle: "À Monsieur F.-M. Vigouroux",
    content: `À MONSIEUR F.-M. VIGOUROUX
PROFESSEUR D'ÉCRITURE SAINTE AU SÉMINAIRE SAINT-SULPICE

HOMMAGE DE VIVE RECONNAISSANCE ET DE FILIAL DÉVOUEMENT

Cher et vénéré Maître,

La Providence vous plaçait il y a vingt-cinq ans sur ma route, pour donner à ma vie morale et à mes études, par votre intermédiaire, une direction décisive.

Je n'ai oublié ni votre premier regard, ni votre première parole ; ni ces commentaires allemands de la Bible que je pus lire, grâce à vous, pour la première fois, et qui ouvrirent à mon ignorance naïve des horizons si nouveaux, si séduisants ; ni vos cours d'Écriture sainte et d'hébreu, pour lesquels vous semblez avoir adopté la devise du célèbre professeur hollandais : Mijn leeren is speelen, « Mon enseignement est une récréation ».

Quand j'eus quitté le séminaire, vous avez encouragé mes premières compositions bibliques, qui devaient, me disiez-vous avec bonté, contribuer à développer mes goûts naissants.

Quand le moment fut venu pour moi d'enseigner à mon tour l'Écriture sainte et la langue sacrée, vos conseils, non moins sages que dévoués, ne me firent jamais défaut.

Puis, un jour, votre autorité paternelle, à laquelle je ne sais rien refuser, plaçait sur mes épaules un fardeau tout à la fois bien pesant et bien doux : vous me demandiez de commenter successivement les quatre Évangiles.

Enfin, avant même que cette tâche importante fût terminée, vous vous entendiez avec mes éditeurs actuels pour m'en imposer une autre, plus considérable encore, puisqu'elle consistait à interpréter toute la Bible. Vous vous souvenez de mes hésitations, et de la manière dont vous en avez triomphé. Mais je n'acceptai qu'à la condition expresse de vous dédier cette œuvre, qui sera la principale de ma vie.

Elle est vôtre, du reste, à tous les points de vue, puisque vous l'avez préparée de longue main en faisant mon éducation scripturaire, directement provoquée, constamment aidée. En effet, ce n'est pas seulement au frontispice du premier volume que l'on trouvera votre nom : presque chaque page le redira à mes lecteurs, puisque sans cesse, — et ce sera mon grand bonheur, — je devrai les renvoyer à vos savants ouvrages : La Bible et les découvertes modernes, le Manuel biblique, les Mélanges bibliques, Les Livres saints et la critique rationaliste, qui ont fait et qui feront encore tant de bien.

Durant les dernières heures que nous avons passées ensemble, vous m'avez dit gracieusement : « Puisse votre commentaire durer autant que celui de Ménochius ! » Je désire en effet qu'il dure de longues années, non seulement pour qu'il fasse connaître et aimer les saintes Écritures, — si je réussis à atteindre mon idéal, — mais encore pour qu'il soit un monument de ma vive reconnaissance et de mon affection filiale à votre égard.

L.-CL. FILLION
Lyon, le 27 décembre 1887.`
  },
  avant_propos: {
    title: "Avant-propos",
    subtitle: "Par L.-Cl. Fillion",
    content: `AVANT-PROPOS

I. La dédicace et la lettre qui précèdent indiquent suffisamment l'origine de l'ouvrage dont nous publions en ce moment la première partie. De nous-même, nous aurions difficilement osé assumer la responsabilité d'une telle entreprise. Néanmoins, à peine à l'œuvre, et malgré des difficultés chaque jour renaissantes, nous nous sommes livré à notre noble tâche con amore, et aussi entièrement que le permettaient nos autres occupations.

II. Notre but est de procurer au clergé, spécialement aux jeunes lévites et aux prêtres du ministère, qui n'ont pas le temps de lire les grands traités exégétiques, un commentaire succinct de toute la Bible, écrit en langue française.

Quoique destinés plus directement aux ecclésiastiques, nos volumes s'adressent cependant aussi aux laïques instruits qui ont du goût pour les études scripturaires.

III. Ce but nous imposait une méthode spéciale, dont voici les principaux linéaments.

1° Comme les travaux analogues parus antérieurement en France et ailleurs, notre publication comprendra trois parties distinctes : le texte latin de la Vulgate ; en regard, la traduction de Sacy, souvent retouchée et rendue plus conforme au texte ; les notes, qui formeront naturellement le corps de l'ouvrage. De courtes introductions seront placées en tête de chaque livre.

2° Pour les notes, une rédaction sobre et concise était strictement obligatoire. Nous avons donc fait de constants efforts pour n'insérer aucun détail inutile. Mais nous nous sommes dit, d'autre part, que concis n'est pas synonyme d'incomplet, et que brièveté ne signifie aridité.

Quoique brèves, les notes seront donc aussi complètes que possible, et toujours au niveau de la science biblique contemporaine. Nous avons tâché de les rendre suggestives, comme s'expriment les Anglais, de manière à ouvrir au lecteur des horizons multiples, et à exciter en lui le désir des recherches et des méditations approfondies.

3° Nous avons eu surtout en vue de bien faire connaître le sens littéral des saints Livres, en appuyant, ainsi qu'il convient, sur les passages difficiles et sur les textes les plus importants sous le rapport théologique.

4° Les renseignements historiques, géographiques, archéologiques, scientifiques, utiles à l'intelligence du sens, seront toujours fidèlement fournis. Nous signalerons de même les divergences des textes originaux, quand elles présenteront quelque gravité ou quelque intérêt. De fréquentes indications bibliographiques renverront le lecteur aux ouvrages spéciaux, surtout aux écrits de nos meilleurs commentateurs catholiques de tous les temps, qui serviront constamment de guides.

Sans vouloir réfuter une à une les objections des hérétiques et des incrédules, nous les indiquerons à l'occasion, et nous en donnerons une solution rapide.

Nous nous proposions aussi de noter d'un mot, comme nous l'avons fait dans nos commentaires sur les saints Évangiles, les chefs-d'œuvre artistiques et littéraires inspirés par de nombreux passages de la Bible. Mais les matériaux de ce genre se sont tellement multipliés entre nos mains, qu'il nous a semblé préférable d'en faire plus tard une œuvre apologétique de longue haleine, pour l'exécution de laquelle nous nous sommes dès maintenant assuré le concours de deux amis intelligents et dévoués.

5° Nous avons été préoccupé sans cesse de faciliter la marche du lecteur par des divisions et subdivisions multiples, analogues à celles de notre Biblia sacra récemment parue. Livres ou parties, sections, paragraphes ; dans les paragraphes, des alinéas numérotés 1°, 2°, etc. ; dans ces alinéas, d'autres séries plus petites de versets : le tout muni de titres, et, pour ainsi dire, dûment étiqueté. Ces groupements équivalent souvent à un commentaire, et ils forment un des principaux avantages de l'exégèse contemporaine. Saint François de Sales aurait dit que ce sont des allées et des sentiers, qui permettent de se promener à l'aise dans le beau jardin des Écritures.

IV. La nécessité d'un ouvrage de ce genre n'est pas douteuse. Depuis d'assez longues années il est réclamé par les membres du clergé français, plus spécialement par les professeurs et les élèves des grands séminaires. On trouve à bon droit Allioli insuffisant, et Ménochius, quoique si excellent, si judicieux, a besoin d'être rajeuni ou plutôt refondu.

V. On a bien voulu penser que nous étions « préparé à ce grand œuvre par seize années d'enseignement exégétique, par la composition de plusieurs ouvrages scripturaires importants, par la lecture de la plupart des commentaires anciens et contemporains ». Du moins, nous avons reçu de hauts et précieux encouragements.

Nos notes de professeur contiennent déjà la plus grande partie de notre travail. La rédaction définitive et la publication des volumes seront, s'il plaît à Dieu, assez rapides.

Daigne l'Auteur des divines Écritures nous donner, à cette fin, lumière et sainteté !

L.-CL. FILLION
Lyon, le 17 décembre 1887.`
  },
  introduction: {
    title: "Introduction",
    subtitle: "L.-Cl. Fillion \u2014 Tome I",
    html: INTRODUCTION_HTML
  },
  mode_emploi: {
    title: "Mode d'emploi",
    subtitle: "Comment utiliser cette application",
    content: `MODE D'EMPLOI DE L'APPLICATION BIBLE FILLION

Bienvenue dans l'application Bible Fillion 1920 !

📖 NAVIGATION
• Utilisez le menu (☰) pour accéder à toutes les fonctionnalités
• La Table des Matières vous permet de naviguer entre les livres et chapitres

🔍 RECHERCHE
• Accédez à la Recherche depuis le menu
• Tapez un mot ou une expression pour trouver les versets correspondants

📑 GARDE-PAGES (Premium)
• Sauvegardez votre position de lecture
• Retrouvez-les facilement dans le menu

🖍️ SURLIGNAGES (Premium)
• Sélectionnez du texte pour le surligner
• Choisissez une couleur

📅 LECTURES DU JOUR
• Les lectures liturgiques sont mises à jour chaque jour à 5h

⭐ PREMIUM
• Accédez à toutes les fonctionnalités avancées
• Paiement sécurisé par carte bancaire

Bonne lecture et méditation !`
  }
};

export const getPreface = (section) => {
  return PREFACE_CONTENT[section] || null;
};

// ===== Push notifications (lecture du jour + rappel) =====
// Uses direct fetch to PostgREST — more reliable than Supabase JS client for upserts
export const upsertPushPrefs = async (subscription, prefs = {}, userId = null) => {
  if (!subscription) return false;
  try {
    const json = subscription.toJSON ? subscription.toJSON() : {};
    let p256dh = json.keys?.p256dh || '';
    let auth = json.keys?.auth || '';

    if (!p256dh && subscription.getKey) {
      const rawKey = subscription.getKey('p256dh');
      if (rawKey) {
        p256dh = btoa(String.fromCharCode.apply(null, new Uint8Array(rawKey)))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      }
    }
    if (!auth && subscription.getKey) {
      const rawAuth = subscription.getKey('auth');
      if (rawAuth) {
        auth = btoa(String.fromCharCode.apply(null, new Uint8Array(rawAuth)))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      }
    }

    const endpoint = subscription.endpoint || json.endpoint;
    if (!endpoint || !p256dh || !auth) {
      console.warn('Upsert push prefs: missing fields', { endpoint: !!endpoint, p256dh: !!p256dh, auth: !!auth });
      return false;
    }

    const payload = [{
      endpoint,
      p256dh,
      auth,
      user_id: userId || null,
      ...prefs,
      updated_at: new Date().toISOString(),
    }];

    // Direct fetch to PostgREST (bypasses JS client caching/middleware issues)
    const r = await fetch(`${SUPABASE_URL}/rest/v1/push_subscriptions?on_conflict=endpoint`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal,resolution=merge-duplicates',
      },
      body: JSON.stringify(payload),
    });

    if (!r.ok) {
      const errText = await r.text();
      console.warn('Upsert push prefs HTTP error:', r.status, errText);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('Upsert push prefs error:', e);
    return false;
  }
};

export const deletePushSubscription = async (endpoint) => {
  if (!endpoint) return;
  try {
    await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
  } catch (e) {
    console.warn('Delete push sub warning:', e);
  }
};
